import React from "react";
import {autobind} from "core-decorators";
import {Button, Table, Typography} from "antd";
import {PlusOutlined, TeamOutlined} from "@ant-design/icons";
import {action, observable} from "mobx";
import {inject, observer} from "mobx-react";
import {gql} from "@apollo/client";
import {RouteComponentProps} from "react-router-dom";

import "./Ensembles.scss";
import {EnsemblesQuery, EnsemblesQueryVariables} from "@/graphql/types";
import CreateEnsembleModal from "./CreateEnsembleModal";
import PageLayout from "@/components/PageLayout/PageLayout";
import EnsemblesActions from "./EnsemblesActions";
import Store from "@/stores";

export interface IEnsemblesProps extends RouteComponentProps {
  store?: Store;
}

@inject("store")
@autobind
@observer
export default class Ensembles extends React.Component<IEnsemblesProps> {
  @observable createEnsembleModalOpen: boolean = false;
  @observable ensembles?: EnsemblesQuery["ensembles"];

  async componentDidMount() {
    await this.fetchEnsembles();
  }

  async closeCreateEnsembleModal() {
    this.setCreateEnsembleModalOpen(false);
    await Promise.all([
      this.fetchEnsembles(), // refresh users to find new invitations
      this.props.store!.session.fetchSessionData(), // refresh ensembles in the session
    ]);
  }

  async fetchEnsembles() {
    const store = this.props.store!;
    const result = await store.apollo.client.query<EnsemblesQuery, EnsemblesQueryVariables>({
      query: gql`
        query EnsemblesQuery($userId: Int!) {
          ensembles: choirs(where: {owner_id: {_eq: $userId}}) {
            id
            name
            pieces_aggregate {
              aggregate {
                count
              }
            }
            user_choirs_aggregate {
              aggregate {
                count
              }
            }
            user_invitations_aggregate(where: {status: {_eq: "pending"}}) {
              aggregate {
                count
              }
            }
          }
        }
      `,
      variables: {userId: store.session.voicefulUser!.id},
    });
    this.setEnsembles(result.data.ensembles);
  }

  openCreateEnsembleModal() {
    this.setCreateEnsembleModalOpen(true);
  }

  @action
  setCreateEnsembleModalOpen(open: boolean) {
    this.createEnsembleModalOpen = open;
  }

  @action
  setEnsembles(ensembles?: EnsemblesQuery["ensembles"]) {
    this.ensembles = ensembles;
  }

  public render() {
    const {session} = this.props.store!;
    return (
      <PageLayout>
        <div className="Ensembles">
          <div className="title-and-buttons">
            <Typography.Title level={3} className="title">
              <TeamOutlined className="title-icon" />
              My Ensembles
            </Typography.Title>
            {(session.hasPlanEnsemble || session.hasPlanPro) && (
              <Button type="primary" icon={<PlusOutlined />} size="small" onClick={this.openCreateEnsembleModal}>
                Create ensemble
              </Button>
            )}
          </div>
          <CreateEnsembleModal open={this.createEnsembleModalOpen} onClose={this.closeCreateEnsembleModal} />
          <Table<EnsemblesQuery["ensembles"][0]>
            // we need to create a new array to trigger a re-render of the table component
            dataSource={this.ensembles?.slice()}
            rowKey="id"
            loading={!this.ensembles}
            pagination={false}
            scroll={{x: true}}
          >
            <Table.Column<EnsemblesQuery["ensembles"][0]>
              title="Actions"
              key="actions"
              render={(_, ensemble) => <EnsemblesActions ensemble={ensemble} ensembles={this.ensembles!} />}
            />
            <Table.Column<EnsemblesQuery["ensembles"][0]> title="Name" dataIndex="name" />
            <Table.Column<EnsemblesQuery["ensembles"][0]>
              title="Members"
              dataIndex={["user_choirs_aggregate", "aggregate", "count"]}
              render={(_, ensemble) =>
                (ensemble.user_choirs_aggregate.aggregate?.count || 0) +
                (ensemble.user_invitations_aggregate.aggregate?.count || 0)
              }
            />
            <Table.Column<EnsemblesQuery["ensembles"][0]>
              title="Pieces"
              dataIndex={["pieces_aggregate", "aggregate", "count"]}
            />
          </Table>
        </div>
      </PageLayout>
    );
  }
}
